import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { visitasApi, type Visita } from '@/lib/api/visitas';
import { liderancasApi } from '@/lib/api/liderancas';
import { adminApi } from '@/lib/api/admin';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { toast } from '@/lib/stores/toastStore';
import { formatarData } from '@/lib/format';
import { Drawer } from '@/design-system/Drawer';
import { Anexos } from '../anexos/Anexos';
import { SeletorParticipantes } from './SeletorParticipantes';
import { Info } from '@/design-system/Info';

const STATUS: Array<{ v: Visita['status']; l: string; chip: string }> = [
  { v: 'planejada', l: 'Planejada', chip: 'neutral' },
  { v: 'concluida', l: 'Concluída', chip: 'good' },
  { v: 'cancelada', l: 'Cancelada', chip: 'crit' },
];

const rotuloStatus = (s: Visita['status']) => STATUS.find((x) => x.v === s)?.l || s;
const chipStatus = (s: Visita['status']) => STATUS.find((x) => x.v === s)?.chip || 'neutral';

const dataHora = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

export function VisitasPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();

  const [statusFiltro, setStatusFiltro] = useState<Visita['status'] | ''>('');
  const [selecionada, setSelecionada] = useState<Visita | null>(null);
  const [novaAberta, setNovaAberta] = useState(false);
  const [relatorio, setRelatorio] = useState('');
  const [resultado, setResultado] = useState('');
  const [proximos, setProximos] = useState('');

  /**
   * `?nova=1` abre o formulário de nova visita já aberto — mesma convenção do
   * `?nova=1` de Ações (`AcoesPage`), que é o que permite ao Resumo ter o botão
   * "Nova visita" do protótipo (proto:855, `openDrawer('nova-visita')`; é o
   * mesmo formulário do botão "Nova visita" desta tela, proto:892).
   *
   * O parâmetro é consumido e apagado da URL com `replace`: sem isso, recarregar
   * a página reabriria o formulário para sempre.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!searchParams.get('nova')) return;
    setNovaAberta(true);
    const p = new URLSearchParams(searchParams);
    p.delete('nova');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  // Ao trocar de visita, os campos passam a refletir o que ESTA visita tem
  // gravado. Sem isto, o texto digitado numa visita reapareceria na próxima
  // que fosse aberta — e seria salvo nela.
  useEffect(() => {
    setResultado(selecionada?.relatorio_campo ?? '');
    setProximos(selecionada?.proximos_passos ?? '');
  }, [selecionada?.id, selecionada?.relatorio_campo, selecionada?.proximos_passos]);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: visitas = [], isLoading } = useQuery({
    queryKey: ['visitas'],
    queryFn: () => visitasApi.list(token),
    enabled: !!token,
  });

  const { data: liderancas = [] } = useQuery({
    queryKey: ['liderancas', {}],
    queryFn: () => liderancasApi.list({}, token),
    enabled: !!token,
  });

  const { data: usuarios = [] } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => adminApi.listUsuarios(token),
    enabled: !!token,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['visitas'] });

  const criar = useMutation({
    mutationFn: (dados: any) => visitasApi.create(dados, token),
    onSuccess: () => {
      invalidar();
      toast('Visita agendada.');
      setNovaAberta(false);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao agendar a visita.')),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Visita> }) =>
      visitasApi.update(id, dados, token),
    onSuccess: (v) => {
      invalidar();
      toast('Visita atualizada.');
      setSelecionada(v);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a visita.')),
  });

  const checkin = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) =>
      visitasApi.doCheckin(id, dados, token),
    onSuccess: (v) => {
      invalidar();
      toast('Check-in registrado com a localização.');
      setSelecionada(v);
      setRelatorio('');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao registrar o check-in.')),
  });

  /**
   * O "Salvar" do protótipo (proto:1303): resultado, próximos passos e
   * situação numa requisição só.
   *
   * Uma chamada, e não três PUTs em sequência: com três, a falha do segundo
   * deixa a visita marcada como concluída sem o resultado que a justifica —
   * e nada na tela diria que ficou pela metade.
   */
  const concluir = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) =>
      visitasApi.concluir(id, dados, token),
    onSuccess: (v) => {
      invalidar();
      toast('Visita concluída com o resultado registrado.');
      setSelecionada(v);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao concluir a visita.')),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => visitasApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Visita removida.');
      setSelecionada(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a visita.')),
  });

  const lista = visitas.filter((v) => !statusFiltro || v.status === statusFiltro);
  const contar = (s: Visita['status']) => visitas.filter((v) => v.status === s).length;
  const comCheckin = visitas.filter((v) => v.checkin_at).length;

  const nomeLideranca = (id?: string) => liderancas.find((l) => l.id === id)?.nome || '—';
  const nomeAssessor = (id?: string) => usuarios.find((u) => u.id === id)?.nome || id || '—';

  /**
   * O check-in usa a geolocalização do dispositivo — é a prova de que a equipe
   * esteve no território, não uma marcação manual.
   */
  const fazerCheckin = (v: Visita) => {
    if (!navigator.geolocation) {
      toast('Este dispositivo não expõe geolocalização.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        checkin.mutate({
          id: v.id,
          dados: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            relatorio_campo: relatorio.trim() || undefined,
          },
        });
      },
      () => toast('Não foi possível obter a localização. Autorize o acesso e tente de novo.'),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="page" id="p-visitas">
      <div className="head">
        <div>
          <h1>Agenda interna</h1>
          <p>
            Presença territorial registrada: participantes, fotos, resultado e próximos passos. O
            check-in é georreferenciado, e concluir a visita exige registrar o resultado.
          </p>
        </div>
        <div className="act">
          <button className="btn pri" onClick={() => setNovaAberta(true)}>
            + Nova visita
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Planejadas</div>
          <div className="v">{contar('planejada')}</div>
          <div className="d">aguardando execução</div>
        </div>
        <div className="card kpi">
          <div className="l">Concluídas</div>
          <div className="v" style={{ color: 'var(--good)' }}>{contar('concluida')}</div>
          <div className="d">de {visitas.length} registradas</div>
        </div>
        <div className="card kpi">
          <div className="l">Com check-in</div>
          <div className="v" style={{ color: 'var(--accent)' }}>{comCheckin}</div>
          <div className="d">presença comprovada por GPS</div>
        </div>
        <div className="card kpi">
          <div className="l">Territórios visitados</div>
          <div className="v">{new Set(visitas.map((v) => v.bairro).filter(Boolean)).size}</div>
          <div className="d">de {bairros.length} do mandato</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters">
        <span
          className={`chip neutral chipbtn ${statusFiltro === '' ? 'on' : ''}`}
          onClick={() => setStatusFiltro('')}
        >
          Todas ({visitas.length})
        </span>
        {STATUS.map((s) => (
          <span
            key={s.v}
            className={`chip neutral chipbtn ${statusFiltro === s.v ? 'on' : ''}`}
            onClick={() => setStatusFiltro(s.v)}
          >
            {s.l} ({contar(s.v)})
          </span>
        ))}
      </div>

      {/* Tabela */}
      <div className="card flat tw">
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Território</th>
              <th>Liderança</th>
              <th>Assessor</th>
              <th>Check-in</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((v) => (
              <tr key={v.id} className="row" style={{ cursor: 'pointer' }} onClick={() => setSelecionada(v)}>
                <td className="mono" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                  {dataHora(v.data_visita)}
                </td>
                <td>
                  <b>{v.bairro}</b>
                  <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{v.municipio}</div>
                </td>
                <td>{nomeLideranca(v.lideranca_id)}</td>
                <td>{nomeAssessor(v.assessor_id)}</td>
                <td>
                  {v.checkin_at ? (
                    <span className="chip good" title={`${v.latitude}, ${v.longitude}`}>
                      {dataHora(v.checkin_at)}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--ink3)', fontSize: 12 }}>—</span>
                  )}
                </td>
                <td>
                  <span className={`chip ${chipStatus(v.status)}`}>{rotuloStatus(v.status)}</span>
                </td>
              </tr>
            ))}

            {isLoading && (
              <tr>
                <td colSpan={6} className="empty">
                  Carregando visitas...
                </td>
              </tr>
            )}
            {!isLoading && lista.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  {visitas.length === 0
                    ? 'Nenhuma visita registrada. Use + Nova visita para agendar a primeira.'
                    : 'Nenhuma visita com esse status.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Drawer: ficha */}
      {selecionada && (
        <Drawer
          aberto
          titulo={`Visita · ${selecionada.bairro}`}
          aoFechar={() => setSelecionada(null)}
        >
              <div className="filters" style={{ marginBottom: 12 }}>
                <span className={`chip ${chipStatus(selecionada.status)}`}>
                  {rotuloStatus(selecionada.status)}
                </span>
                <span className="chip eleg">{selecionada.bairro}</span>
                {selecionada.checkin_at && <span className="chip good">check-in feito</span>}
              </div>

              <dl className="dl">
                <dt>Agendada para</dt>
                <dd>{dataHora(selecionada.data_visita)}</dd>
                <dt>Assessor em campo</dt>
                <dd>{nomeAssessor(selecionada.assessor_id)}</dd>
                <dt>Liderança</dt>
                <dd>{nomeLideranca(selecionada.lideranca_id)}</dd>
                <dt>Check-in</dt>
                <dd>
                  {selecionada.checkin_at ? (
                    <>
                      {dataHora(selecionada.checkin_at)}
                      <div style={{ fontSize: 11, color: 'var(--ink3)' }} className="mono">
                        {selecionada.latitude?.toFixed(5)}, {selecionada.longitude?.toFixed(5)}
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${selecionada.latitude},${selecionada.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 11, color: 'var(--accent)' }}
                      >
                        ver no mapa
                      </a>
                    </>
                  ) : (
                    'ainda não realizado'
                  )}
                </dd>
                <dt>Criada em</dt>
                <dd>{formatarData(selecionada.created_at)}</dd>
              </dl>

              {/* Participantes — `partPicker` do protótipo (proto:1290) */}
              <SeletorParticipantes
                visitaId={selecionada.id}
                bairro={selecionada.bairro}
                municipio={selecionada.municipio}
                liderancaId={selecionada.lideranca_id}
                liderancaNome={
                  selecionada.lideranca_id ? nomeLideranca(selecionada.lideranca_id) : undefined
                }
              />

              {/* Evidências — `EVID` do protótipo (proto:1290). Mesmo componente
                  de anexo das solicitações e dos contatos; o que muda é o dono.
                  O envio sai pela rota da agenda interna porque ela aceita o
                  `articulador`, que faz o check-in geolocalizado: quem esteve na
                  visita é quem anexa a prova de ter estado, e é o nome dele que
                  fica em `created_by` e na auditoria. */}
              <Anexos
                donoTipo="visita"
                donoId={selecionada.id}
                titulo="Evidências"
                tipoPadrao="Relatório público"
                ajuda="Fotos, lista de presença, ata, áudio."
                rotaEnvio={`/gab/agenda-interna/${selecionada.id}/evidencias`}
              />

              <div className="field" style={{ marginTop: 16 }}>
                <label htmlFor="vs-res">Resultado (o que foi tratado)</label>
                <textarea
                  id="vs-res"
                  className="in"
                  style={{ minHeight: 90 }}
                  value={resultado}
                  onChange={(e) => setResultado(e.target.value)}
                />
              </div>

              <div className="field">
                {/*
                  O ícone fica FORA do `label`: um `button` dentro dele passa a
                  ser rotulado pelo mesmo texto, e `getByLabelText('Próximos
                  passos')` deixa de achar um campo só.

                  Última oração reescrita em relação ao protótipo (proto:1303),
                  que diz "a visita entra na linha do tempo de cada
                  participante". Linha do tempo genérica do contato não existe no
                  sistema; o que existe é "Visitas deste contato" na ficha.
                */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label htmlFor="vs-prox">Próximos passos</label>
                  <span data-testid="converter-em-acao">
                    <Info titulo="Converter em ação">
                      Cada próximo passo pode virar uma solicitação ou uma ação do mandato; a
                      visita entra nas visitas registradas na ficha de cada participante.
                    </Info>
                  </span>
                </div>
                <input
                  id="vs-prox"
                  className="in"
                  value={proximos}
                  onChange={(e) => setProximos(e.target.value)}
                />
              </div>

              <button
                className="btn pri"
                style={{ marginTop: 10 }}
                disabled={concluir.isPending}
                onClick={() => {
                  if (!resultado.trim()) {
                    toast('Escreva o resultado antes de concluir a visita.');
                    return;
                  }
                  concluir.mutate({
                    id: selecionada.id,
                    dados: {
                      relatorio_campo: resultado.trim(),
                      proximos_passos: proximos.trim(),
                    },
                  });
                }}
              >
                {concluir.isPending ? 'Salvando...' : 'Salvar'}
              </button>

              {/*
                O que Salvar NÃO faz. O protótipo, no mesmo clique, somava 10 à
                saúde da liderança e mexia em contadores de recência e
                frequência; nada disso é replicado (ver
                `agenda-interna.service.ts`, `concluir()`). A explicação fica
                ancorada no próprio botão, que é o que ela descreve.
              */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                {selecionada.lideranca_id && (
                  <span data-testid="lacuna-saude-lideranca">
                    <Info titulo="O que salvar não atualiza na liderança">
                      Concluir a visita não recalcula a saúde do vínculo da liderança, e isso é
                      deliberado: a saúde é um número que o gabinete digita na tela de Lideranças,
                      não um score calculado, e somar pontos a cada visita sobrescreveria em
                      silêncio a avaliação de quem o preencheu. Recência e frequência de contato
                      com a liderança também não são atualizadas, porque não existem como dado no
                      sistema: não há coluna nem cálculo para elas.
                    </Info>
                  </span>
                )}
                {/*
                  Fora da condicional de propósito: o efeito sobre os
                  participantes vale para toda visita, com liderança ou sem.
                */}
                <span data-testid="lacuna-participantes">
                  <Info titulo="O que salvar registra dos participantes">
                    A presença de cada participante nesta visita, e é dela que sai a lista "Visitas
                    deste contato" na ficha de cada um. O protótipo também zerava um campo "último
                    contato" na ficha; esse campo não existe aqui, e nenhum é criado: a última
                    visita de uma pessoa se lê na própria lista, sem um segundo lugar para a mesma
                    verdade.
                  </Info>
                </span>
              </div>

              {/* Check-in */}
              {!selecionada.checkin_at && (
                <>
                  <div className="field" style={{ marginTop: 16 }}>
                    <label>Relatório de campo (opcional no check-in)</label>
                    <textarea
                      className="in"
                      style={{ minHeight: 90 }}
                      value={relatorio}
                      onChange={(e) => setRelatorio(e.target.value)}
                      placeholder="O que foi observado e combinado na visita"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                    <button
                      className="btn pri"
                      disabled={checkin.isPending}
                      onClick={() => fazerCheckin(selecionada)}
                    >
                      {checkin.isPending ? 'Registrando...' : '📍 Fazer check-in'}
                    </button>
                    <Info titulo="O que o check-in registra">
                      Captura a localização do dispositivo no momento do registro. É a comprovação
                      de presença no território, e por isso não é editável depois.
                    </Info>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                <div className="field" style={{ flex: 1 }}>
                  <label style={{ fontSize: 11 }}>Situação</label>
                  <select
                    className="in"
                    value={selecionada.status}
                    disabled={atualizar.isPending}
                    onChange={(e) =>
                      atualizar.mutate({
                        id: selecionada.id,
                        dados: { status: e.target.value as Visita['status'] },
                      })
                    }
                  >
                    {STATUS.map((s) => (
                      <option key={s.v} value={s.v}>
                        {s.l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                className="btn"
                style={{ color: 'var(--crit)', marginTop: 12 }}
                disabled={excluir.isPending}
                onClick={() => {
                  if (window.confirm(`Remover a visita a ${selecionada.bairro}?`)) {
                    excluir.mutate(selecionada.id);
                  }
                }}
              >
                Excluir visita
              </button>
        </Drawer>
      )}

      {/* Drawer: nova */}
      {novaAberta && (
        <NovaVisita
          bairros={bairros.map((b) => b.n)}
          liderancas={liderancas}
          usuarios={usuarios}
          salvando={criar.isPending}
          onCancelar={() => setNovaAberta(false)}
          onSalvar={(dados) => criar.mutate(dados)}
        />
      )}
    </div>
  );
}

function NovaVisita({
  bairros,
  liderancas,
  usuarios,
  salvando,
  onCancelar,
  onSalvar,
}: {
  bairros: string[];
  liderancas: Array<{ id: string; nome: string; bairro?: string }>;
  usuarios: Array<{ id: string; nome: string }>;
  salvando: boolean;
  onCancelar: () => void;
  onSalvar: (dados: any) => void;
}) {
  const agora = new Date();
  const [bairro, setBairro] = useState(bairros[0] || '');
  const [data, setData] = useState(agora.toISOString().slice(0, 10));
  const [horario, setHorario] = useState('09:00');
  const [assessorId, setAssessorId] = useState(usuarios[0]?.id || '');
  const [liderancaId, setLiderancaId] = useState('');

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bairro) {
      toast('Selecione o território da visita.');
      return;
    }
    if (!assessorId) {
      toast('Selecione o assessor que vai a campo.');
      return;
    }
    onSalvar({
      assessor_id: assessorId,
      lideranca_id: liderancaId || undefined,
      municipio: 'Fortaleza',
      bairro,
      data_visita: new Date(`${data}T${horario}:00`).toISOString(),
    });
  };

  return (
    <Drawer aberto titulo="Nova visita de campo" aoFechar={onCancelar}>
      <form onSubmit={submeter} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="field">
              <label>Território *</label>
              <select className="in" value={bairro} onChange={(e) => setBairro(e.target.value)}>
                {bairros.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid g2">
              <div className="field">
                <label>Data</label>
                <input
                  type="date"
                  className="in"
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Horário</label>
                <input
                  type="time"
                  className="in"
                  value={horario}
                  onChange={(e) => setHorario(e.target.value)}
                />
              </div>
            </div>

            <div className="field">
              <label>Assessor em campo *</label>
              <select className="in" value={assessorId} onChange={(e) => setAssessorId(e.target.value)}>
                <option value="">Selecione</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Liderança a visitar</label>
              <select className="in" value={liderancaId} onChange={(e) => setLiderancaId(e.target.value)}>
                <option value="">Nenhuma em específico</option>
                {liderancas
                  .filter((l) => !bairro || !l.bairro || l.bairro === bairro)
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.nome}
                      {l.bairro ? ` · ${l.bairro}` : ''}
                    </option>
                  ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 6, alignItems: 'center' }}>
              <button type="button" className="btn" onClick={onCancelar}>
                Cancelar
              </button>
              <button type="submit" className="btn pri" disabled={salvando}>
                {salvando ? 'Agendando...' : 'Agendar visita'}
              </button>
              <Info titulo="Quando o relatório é preenchido">
                O relatório e a comprovação de presença são registrados no check-in, feito no local
                pelo assessor.
              </Info>
            </div>
      </form>
    </Drawer>
  );
}
